import React, { useCallback, useEffect, useState } from "react";
import { Box, Card, CardContent, CardHeader, Fade } from "@mui/material";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import EditIcon from "@mui/icons-material/Edit";
import Grid from "@mui/material/Grid";
import {
  getLibraryType,
  useOrganizationProfile,
} from "../organizationServices";
import { notBlank } from "../../util/ext";
import PanelLoadingIndicator from "../../components/PanelLoadingIndicator";
import { fetchSecurityUser } from "../../util/fetchUtils";
import { mergeRight } from "ramda";
import { useSelector } from "react-redux";
import LabeledReadOnly from "../../components/LabeledReadOnly";
import YesNoCard from "../../components/YesNoCard";
import makeStyles from "@mui/styles/makeStyles";
import ReadOnlyField from "../../components/ReadOnlyField";
import EditOrganizationProfileDialog from "../edit/EditOrganizationProfileDialog";

const userCache = new Map();
const lookupUser = (auth, userId) => {
  if (!userCache.has(userId)) {
    userCache.set(userId, fetchSecurityUser(auth)(userId));
  }

  return userCache.get(userId);
};

const useStyles = makeStyles(theme => ({
  header: {
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
  },

  content: {
    paddingTop: 0,
  },
}));

const OrganizationProfileCard = ({ id, onProfileLoaded }) => {
  const auth = useSelector(state => state.auth);
  const classes = useStyles();
  const [organization, setOrganization] = useState(null);

  const [saveAlert, setSaveAlert] = useState(false);
  const timerAutoHide = React.useRef();
  const setAutoHideTimer = useCallback(timeoutDuration => {
    clearTimeout(timerAutoHide.current);
    timerAutoHide.current = setTimeout(() => {
      setSaveAlert(false);
    }, timeoutDuration);
  }, []);

  React.useEffect(() => {
    if (saveAlert) {
      setAutoHideTimer(3000);
    }

    return () => {
      clearTimeout(timerAutoHide.current);
    };
  }, [saveAlert, setAutoHideTimer]);

  const profileResponse = useOrganizationProfile(id);
  useEffect(() => {
    if (profileResponse.isSuccess && notBlank(profileResponse.data)) {
      const fetchUsers = async org => {
        const salesPerson = notBlank(org.salesPersonAccountId)
          ? await lookupUser(auth, org.salesPersonAccountId)
          : null;
        const libraryType = getLibraryType(org.libraryTypeId);

        return mergeRight(org, {
          salesPerson: notBlank(salesPerson) ? salesPerson.username : null,
          libraryType,
        });
      };

      fetchUsers(profileResponse.data).then(setOrganization);
      setOrganization(profileResponse.data);
      onProfileLoaded(profileResponse.data);
    }
  }, [profileResponse.isSuccess, profileResponse.data, auth, onProfileLoaded]);

  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const handleEditClick = useCallback(() => {
    console.log("Handling Edit click...");
    setEditDialogOpen(true);
  }, []);

  const handleEditCancel = useCallback(() => {
    setEditDialogOpen(false);
  }, []);

  const handleEditSave = useCallback(
    async data => {
      await profileResponse.refetch();
      setEditDialogOpen(false);
      setSaveAlert(true);
      console.log("Organization Details Saved...");
      console.log(data);
    },
    [profileResponse],
  );

  return (
    <Card raised elevation={4} style={{ height: "100%" }}>
      <CardHeader
        titleTypographyProps={{
          variant: "h6",
          color: "primary",
        }}
        title="Profile"
        action={
          <Box display="flex" justifyContent="flex-end" alignItems="center">
            <Fade in={saveAlert}>
              <Box pr={5} color="green">
                Update Successful
              </Box>
            </Fade>
            <Tooltip title="Edit Profile">
              <IconButton onClick={handleEditClick} size="large">
                <EditIcon color="primary" fontSize="large" />
              </IconButton>
            </Tooltip>
          </Box>
        }
        classes={{ root: classes.header }}
      />
      <CardContent classes={{ root: classes.content }}>
        {profileResponse.isError && (
          <Box padding={5} fontSize="1.6rem" fontStyle="italic">
            No Profile has been set up.
          </Box>
        )}
        {profileResponse.isLoading && <PanelLoadingIndicator />}
        {profileResponse.isSuccess && notBlank(organization) && (
          <Grid container spacing={3}>
            <Grid item sm={8}>
              <Grid container spacing={3}>
                <Grid item sm={2}>
                  <LabeledReadOnly label="Library Type">
                    <ReadOnlyField>
                      {organization.libraryType &&
                        organization.libraryType.name}
                    </ReadOnlyField>
                  </LabeledReadOnly>
                </Grid>

                <Grid item sm={3}>
                  <LabeledReadOnly label="Sales Person">
                    <ReadOnlyField>{organization.salesPerson}</ReadOnlyField>
                  </LabeledReadOnly>
                </Grid>

                <Grid item sm={4}>
                  <LabeledReadOnly label="OAuth Display Name">
                    <ReadOnlyField>
                      {organization.oauthDisplayName}
                    </ReadOnlyField>
                  </LabeledReadOnly>
                </Grid>

                <Grid item sm={3}>
                  <LabeledReadOnly label="Offline Bookshelf Max">
                    {organization.offlineBookshelfMax}
                  </LabeledReadOnly>
                </Grid>

                <Grid item sm={6}>
                  <LabeledReadOnly label="Marketing Url Name">
                    <ReadOnlyField>
                      {organization.marketingUrlName}
                    </ReadOnlyField>
                  </LabeledReadOnly>
                </Grid>

                <Grid item sm={6}>
                  <LabeledReadOnly label="Library Url">
                    <ReadOnlyField>{organization.libraryUrl}</ReadOnlyField>
                  </LabeledReadOnly>
                </Grid>

                <Grid item sm={6}>
                  <LabeledReadOnly label="Marketing Url">
                    <ReadOnlyField>{organization.marketingUrl}</ReadOnlyField>
                  </LabeledReadOnly>
                </Grid>

                <Grid item sm={6}>
                  <LabeledReadOnly label="Library Card Url">
                    <ReadOnlyField>{organization.libraryCardUrl}</ReadOnlyField>
                  </LabeledReadOnly>
                </Grid>

                <Grid item sm={12}>
                  <LabeledReadOnly label="Attribution Statement">
                    <ReadOnlyField>
                      {organization.attributionStmt}
                    </ReadOnlyField>
                  </LabeledReadOnly>
                </Grid>
              </Grid>
            </Grid>

            <Grid item sm={4}>
              <Grid container spacing={3}>
                <Grid item sm={6}>
                  <LabeledReadOnly label="Exclude From Reports">
                    <YesNoCard value={organization.excludeFromReports} />
                  </LabeledReadOnly>
                </Grid>

                <Grid item sm={6}>
                  <LabeledReadOnly label="Show IAP">
                    <YesNoCard value={organization.showSelfe} />
                  </LabeledReadOnly>
                </Grid>

                <Grid item sm={6}>
                  <LabeledReadOnly label="Show Buy LInk">
                    <YesNoCard value={organization.showBuyLink} />
                  </LabeledReadOnly>
                </Grid>

                <Grid item sm={6}>
                  <LabeledReadOnly label="IAP Enabled">
                    <YesNoCard value={organization.selfeEnabled} />
                  </LabeledReadOnly>
                </Grid>

                <Grid item sm={6}>
                  <LabeledReadOnly label="Show Share Links">
                    <YesNoCard value={organization.showShareLinks} />
                  </LabeledReadOnly>
                </Grid>

                <Grid item sm={6}>
                  <LabeledReadOnly label="Banner Ad Mobile App">
                    <YesNoCard value={organization.bannerAdMobileApp} />
                  </LabeledReadOnly>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        )}
        {editDialogOpen && (
          <EditOrganizationProfileDialog
            open={editDialogOpen}
            organizationId={id}
            profile={organization}
            onCancel={handleEditCancel}
            onSave={handleEditSave}
          />
        )}
      </CardContent>
    </Card>
  );
};

export default OrganizationProfileCard;
